import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Paper } from '@mui/material';

import EmptyCard from '@components/EmptyCard/EmptyCard';
import Loadable from '@components/Loadable/Loadable';
import {
  handleGetAdminProgramCodeDetails,
  handleGetAdminProgramCodes,
} from '@store/features/adminProgramCodes/actions';
import {
  adminProgramCodeActiveIdSelector,
  adminProgramCodeCreatingNewSelector,
  adminProgramCodeDetailsErrorSelector,
  adminProgramCodeDetailsIsLoadingSelector,
  adminProgramCodeDetailsSelector,
  adminProgramCodesErrorSelector,
  adminProgramCodesIsLoadingSelector,
  adminProgramCodesSelector,
} from '@store/features/adminProgramCodes/selectors';
import {
  removeAdminProgramCodeDetails,
  removeAdminProgramCodeList,
  setAdminProgramCodeCreatingNew,
} from '@store/features/adminProgramCodes/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';

import NewProgramCode from './NewProgramCode/NewProgramCode';
import ProgramCodeDetails from './ProgramCodeDetails/ProgramCodeDetails';
import ProgramCodeList from './ProgramCodeList/ProgramCodeList';

import './style.scss';

const ProgramCodes: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('adminProgramCodes');

  const data = useSelector(adminProgramCodesSelector);
  const isLoading = useSelector(adminProgramCodesIsLoadingSelector);
  const error = useSelector(adminProgramCodesErrorSelector);
  const programCodeId = useSelector(adminProgramCodeActiveIdSelector);
  const programCode = useSelector(adminProgramCodeDetailsSelector);
  const programCodeIsLoading = useSelector(adminProgramCodeDetailsIsLoadingSelector);
  const programCodeError = useSelector(adminProgramCodeDetailsErrorSelector);
  const isCreatingNew = useSelector(adminProgramCodeCreatingNewSelector);

  const handleFetchProgramCodes = useCallback(() => {
    dispatch(handleGetAdminProgramCodes());
  }, [dispatch]);

  const handleFetchProgramCodeDetails = useCallback(
    (programCodeId: number) => {
      dispatch(handleGetAdminProgramCodeDetails(programCodeId));
    },
    [dispatch],
  );

  const handleRetryFetchProgramCodeDetails = useCallback(() => {
    if (programCodeId) handleFetchProgramCodeDetails(programCodeId);
  }, [programCodeId, handleFetchProgramCodeDetails]);

  const handleCreateNew = useCallback(() => {
    dispatch(setAdminProgramCodeCreatingNew());
  }, [dispatch]);

  useEffect(() => {
    handleFetchProgramCodes();
  }, [handleFetchProgramCodes]);

  useEffect(() => {
    eventBus.on([EventType.ADMIN_CREATE_NEW_PROGRAM_CODE_SUCCESS], () => {
      dispatch(removeAdminProgramCodeDetails());
      handleFetchProgramCodes();
    });

    eventBus.on([EventType.ADMIN_EDIT_PROGRAM_CODE_SUCCESS], () => {
      handleFetchProgramCodes();
    });

    return () =>
      eventBus.off([
        EventType.ADMIN_CREATE_NEW_PROGRAM_CODE_SUCCESS,
        EventType.ADMIN_EDIT_PROGRAM_CODE_SUCCESS,
      ]);
  }, [eventBus, dispatch, handleFetchProgramCodes]);

  useEffect(
    () => () => {
      dispatch(removeAdminProgramCodeList());
      dispatch(removeAdminProgramCodeDetails());
    },
    [dispatch],
  );

  return (
    <div className="program-codes">
      <div className="program-codes__header">
        <div className="program-codes__title">{t('admin.axisProgramCodes')}</div>

        <Button onClick={handleCreateNew} variant="contained" className="program-codes__create-new">
          {t('admin.addNewProgramCode')}
        </Button>
      </div>

      <div className="program-codes__body">
        <Paper className="program-codes__list" elevation={0}>
          <Loadable
            isLoading={isLoading}
            error={error && t('errors.programCodeList')}
            onRetry={handleFetchProgramCodes}
          >
            {data && (
              <ProgramCodeList
                data={data}
                activeId={programCodeId}
                onClick={handleFetchProgramCodeDetails}
              />
            )}
          </Loadable>
        </Paper>

        <Paper className="program-codes__details" elevation={0}>
          {isCreatingNew ? (
            <NewProgramCode />
          ) : programCodeId ? (
            <Loadable
              isLoading={programCodeIsLoading}
              error={programCodeError && t('errors.programCodeDetails')}
              onRetry={handleRetryFetchProgramCodeDetails}
            >
              {programCode && <ProgramCodeDetails data={programCode} />}
            </Loadable>
          ) : (
            <EmptyCard
              className="program-codes__details-placeholder"
              borderType="none"
              icon="/images/img-clipboard.svg"
              description={t('admin.programCodeDetailsPlaceholder')}
            />
          )}
        </Paper>
      </div>
    </div>
  );
};

export default ProgramCodes;
